import React from 'react';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

const AboutSection = ({ language }) => {
  const translations = {
    en: {
      title: 'About FitPro Center',
      subtitle: 'Your Premier Fitness Destination',
      mission: 'Our Mission',
      missionText: 'To provide a world-class fitness experience that empowers individuals to achieve their health and wellness goals through expert guidance, state-of-the-art facilities, and a supportive community.',
      facilities: 'Our Facilities',
      cardioZone: 'Cardio Zone',
      cardioDesc: 'Modern treadmills, ellipticals, and bikes',
      strengthTraining: 'Strength Training',
      strengthDesc: 'Free weights and resistance machines',
      groupClasses: 'Group Classes',
      groupDesc: 'Yoga, Pilates, HIIT, and more',
      personalTraining: 'Personal Training',
      personalDesc: 'One-on-one sessions with experts',
      recovery: 'Recovery Zone',
      recoveryDesc: 'Sauna, massage, and relaxation area',
      nutrition: 'Nutrition Center',
      nutritionDesc: 'Healthy smoothies and supplements'
    },
    ar: {
      title: 'حول مركز فيت برو',
      subtitle: 'وجهتك الرئيسية للياقة البدنية',
      mission: 'مهمتنا',
      missionText: 'تقديم تجربة لياقة بدنية عالمية المستوى تمكن الأفراد من تحقيق أهدافهم الصحية واللياقة من خلال التوجيه الخبير والمرافق الحديثة والمجتمع الداعم.',
      facilities: 'مرافقنا',
      cardioZone: 'منطقة الكارديو',
      cardioDesc: 'أجهزة جري وآلات حديثة',
      strengthTraining: 'تدريب القوة',
      strengthDesc: 'الأوزان الحرة وآلات المقاومة',
      groupClasses: 'الفصول الجماعية',
      groupDesc: 'يوجا، بيلاتس، هييت، والمزيد',
      personalTraining: 'التدريب الشخصي',
      personalDesc: 'جلسات فردية مع الخبراء',
      recovery: 'منطقة الاستشفاء',
      recoveryDesc: 'ساونا، تدليك، ومنطقة استرخاء',
      nutrition: 'مركز التغذية',
      nutritionDesc: 'عصائر صحية ومكملات غذائية'
    }
  };

  const t = translations[language];
  const isRTL = language === 'ar';

  const facilities = [
    {
      icon: 'Heart',
      title: t.cardioZone,
      description: t.cardioDesc,
      color: 'bg-red-100 text-red-600'
    },
    {
      icon: 'Dumbbell',
      title: t.strengthTraining,
      description: t.strengthDesc,
      color: 'bg-blue-100 text-blue-600'
    },
    {
      icon: 'Users',
      title: t.groupClasses,
      description: t.groupDesc,
      color: 'bg-purple-100 text-purple-600'
    },
    {
      icon: 'User',
      title: t.personalTraining,
      description: t.personalDesc,
      color: 'bg-green-100 text-green-600'
    },
    {
      icon: 'Sparkles',
      title: t.recovery,
      description: t.recoveryDesc,
      color: 'bg-yellow-100 text-yellow-600'
    },
    {
      icon: 'Apple',
      title: t.nutrition,
      description: t.nutritionDesc,
      color: 'bg-orange-100 text-orange-600'
    }
  ];

  return (
    <section id="about" className={`py-20 bg-gray-50 ${isRTL ? 'rtl' : 'ltr'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">{t.title}</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">{t.subtitle}</p>
        </div>

        {/* Mission and Facility Images */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          {/* Mission Text */}
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-6">{t.mission}</h3>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">{t.missionText}</p>
            
            {/* Key Stats 
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <Icon name="Award" size={24} className="text-green-600" />
                  <span className="text-2xl font-bold text-gray-900">5+</span>
                </div>
                <p className="text-gray-600">Years of Excellence</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <Icon name="MapPin" size={24} className="text-green-600" />
                  <span className="text-2xl font-bold text-gray-900">3</span>
                </div>
                <p className="text-gray-600">Locations</p>
              </div>
            </div>*/}
          </div>

          {/* Facility Images */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-4">
              <Image 
                src="https://images.pixabay.com/photo/2017/08/07/14/02/man-2604149_1280.jpg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop"
                alt="Gym Equipment"
                className="w-full h-48 object-cover rounded-lg shadow-lg"
              />
              <Image 
                src="https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop"
                alt="Group Exercise"
                className="w-full h-32 object-cover rounded-lg shadow-lg"
              />
            </div>
            <div className="space-y-4 pt-8">
              <Image 
                src="https://images.pexels.com/photo/703012/pexels-photo-703012.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop"
                alt="Personal Training"
                className="w-full h-32 object-cover rounded-lg shadow-lg"
              />
              <Image 
                src="https://images.pixabay.com/photo/2016/11/19/12/43/yoga-1839595_1280.jpg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop"
                alt="Yoga Class"
                className="w-full h-48 object-cover rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>

        {/* Facilities Grid */}
        <div>
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-12">{t.facilities}</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {facilities.map((facility, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                <div className={`w-12 h-12 ${facility.color} rounded-lg flex items-center justify-center mb-4`}>
                  <Icon name={facility.icon} size={24} />
                </div>
                <h4 className="text-xl font-semibold text-gray-900 mb-2">{facility.title}</h4>
                <p className="text-gray-600">{facility.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;